import React from 'react';
import { KeyVal } from 'equasion-lib';
import { formatPostalCodeWithCountry } from '@/lib/utils/addressUtils';
import { FiCornerDownRight, FiXCircle } from 'react-icons/fi';
import './ScannedDetails.scss';

const formatEventType = (type) => {
  if (!type) return 'Unknown Event';
  return String(type)
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatEventDateTime = (createdAt) => {
  if (!createdAt) return 'Unknown Time';
  const date = createdAt instanceof Date ? createdAt : new Date(createdAt);
  if (Number.isNaN(date.getTime())) return 'Unknown Time';
  return date.toLocaleString();
};

const sortEventsOldestToNewest = (events: any[] = []) =>
  [...events].sort((a: any, b: any) => {
    const aDate = new Date(a?.createdAt);
    const bDate = new Date(b?.createdAt);
    const aTime = Number.isNaN(aDate.getTime()) ? 0 : aDate.getTime();
    const bTime = Number.isNaN(bDate.getTime()) ? 0 : bDate.getTime();
    return aTime - bTime;
  });

const ScannedDetails = ({
  status,
  zone,
  salesOrder,
  purchaseOrder,
  pedigree,
  serialNumber,
  close,
  inventoryEventHistory
}) => {
  const { name: zoneName } = zone || {};
  const { vendorSalesOrderNumber: salesOrderNumber, client } = salesOrder || {};
  const { companyName: clientName } = client || {};
  const { clientPurchaseOrderNumber, vendor } = purchaseOrder || {};
  const { companyName, billingAddress } = vendor || {};
  const { lineOne, lineTwo, city, state, zipCode, countryCode } = billingAddress || {};
  const { SSCC, CA, PK, ITEM } = pedigree || {};
  const packageType =
    (SSCC && 'SSCC') || (CA && 'CA') || (PK && 'PK') || (ITEM && 'ITEM');

  return (
    <div className="scanned-details">
      <div className="close" onClick={close}>
        <FiXCircle />
      </div>
      <KeyVal
        items={
          [
            {
              key: 'PO Number',
              value: clientPurchaseOrderNumber
            },
            status === 'SHIPPED' &&
              !!salesOrderNumber && {
                key: 'SO Number',
                value: salesOrderNumber
              },
            status === 'SHIPPED' &&
              !!clientName && {
                key: 'Client',
                value: clientName
              },
            {
              key: 'Serial',
              value: serialNumber
            },
            {
              key: 'Type',
              value: packageType
            },
            status === 'STOCKED' &&
              !!zoneName && {
                key: 'Zone',
                value: zoneName
              },
            {
              key: 'Total Products',
              value: ITEM
            },
            {
              key: 'Sold By',
              value: (
                <div style={{ textAlign: 'right' }}>
                  {companyName}
                  <br />
                  {lineOne}
                  <br />
                  {!!lineTwo && (
                    <>
                      {lineTwo}
                      <br />
                    </>
                  )}
                  {city}, {state} {formatPostalCodeWithCountry(zipCode, countryCode)}
                </div>
              )
            },
            !!SSCC && {
              key: 'SSCC',
              value: SSCC
            },
            !!CA && {
              key: <span>{!!SSCC && <FiCornerDownRight />} Cases</span>,
              value: CA
            },
            !!PK && {
              key: (
                <span>
                  {!!CA && (
                    <FiCornerDownRight style={{ marginLeft: SSCC ? '1rem' : '' }} />
                  )}{' '}
                  Packages
                </span>
              ),
              value: PK
            },
            {
              key: <b>Products</b>,
              value: <b>{ITEM}</b>
            }
          ].filter(Boolean) as Array<{ key: React.ReactNode; value: React.ReactNode }>
        }
      />
      <div className="inventory-event-history">
        <h3>Inventory Event History</h3>
        {!inventoryEventHistory?.length ? (
          <p className="inventory-event-history-empty">No inventory events found.</p>
        ) : (
          <ul>
            {sortEventsOldestToNewest(inventoryEventHistory).map(
              ({ id, type, createdAt, user, fromZone }) => {
                const { name } = user || {};
                const formattedDateTime = formatEventDateTime(createdAt);
                // The zone a Pick took the unit from: where it came off the shelf.
                const pickedFromZoneName = type === 'PICK' ? fromZone?.name : null;

                return (
                  <li key={id} className="inventory-event-history-item">
                    <span className="inventory-event-type">{formatEventType(type)}</span>
                    <span className="inventory-event-metadata">
                      {formattedDateTime} by {name || 'Unknown User'}
                    </span>
                    {!!pickedFromZoneName && (
                      <span className="inventory-event-metadata">
                        Picked from {pickedFromZoneName}
                      </span>
                    )}
                  </li>
                );
              }
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ScannedDetails;
